/**
 * Card data validation service
 */

import { CardData, CardType, CardValidationResult } from '../types/CardData';
import { luhnCheck } from './luhnCheck';
import { detectCardType, getExpectedCvvLength, isValidCardLength,getCardTypeName } from './cardTypeDetector';
import { COUNTRIES } from '../data/regionData';

const US_COUNTRY = COUNTRIES.find((c) => c.code === 'US')!;
const VALID_STATE_CODES = new Set(US_COUNTRY.states.map((s) => s.code));

export const CARDHOLDER_NAME_MIN_LENGTH = 2;
export const CARDHOLDER_NAME_MAX_LENGTH = 100;
export const ADDRESS_LINE_MAX_LENGTH = 100;
export const CITY_MAX_LENGTH = 50;
export const POSTAL_CODE_MAX_LENGTH = 15;

/**
 * Flattens validation error messages from a Record into a single array
 * Handles both string and string array values
 * @param errors - Record of field errors (values can be string or string[])
 * @returns Flattened array of all error messages
 */
export function cleanValidationErrors(errors: Record<string, string | string[]>): string[] {
  const cleanedErrors: string[] = [];
  
  for (const messages of Object.values(errors)) {
    const messageArray = Array.isArray(messages) ? messages : [messages];
    cleanedErrors.push(...messageArray);
  }
  
  return cleanedErrors;
}

/**
 * Validates a complete card data object
 * @param cardData - Card data to validate
 * @returns Validation result with errors
 */
export function validateCardData(cardData: Partial<CardData>): CardValidationResult {
  const errors: Record<string, string> = {};

  // Validate card number
  if (!cardData.cardNumber) {
    errors.cardNumber = 'Card Number field is required';
  } else {
    const cardNumberError = validateCardNumber(cardData.cardNumber);
    if (cardNumberError) {
      errors.cardNumber = cardNumberError;
    }
  }

 
  // Validate expiration date (month + year as a combined field)
  if (!cardData.expiryMonth?.trim() && !cardData.expiryYear?.trim()) {
    errors.expiryDate = 'Expiration date must be in MM/YY format';
  } else if (!cardData.expiryMonth?.trim() || !cardData.expiryYear?.trim()) {
    errors.expiryDate = 'Expiration date must be in MM/YY format';
  } else {
    const expiryError = validateExpiryDate(cardData.expiryMonth, cardData.expiryYear);
    if (expiryError) {
      errors.expiryDate = expiryError;
    }
  }

  // Validate CVV
  if (!cardData.cvv) {
    errors.cvv = 'CVV field is required';
  } else {
    const cardType = detectCardType(cardData.cardNumber || '');
    const cvvError = validateCvv(cardData.cvv, cardType);
    if (cvvError) {
      errors.cvv = cvvError;
    }
  }

  // Validate cardholder name
  if (!cardData.cardholderName?.trim()) {
    errors.cardholderName = 'Name on Card field is required';
  } else {
    const nameError = validateCardholderName(cardData.cardholderName);
    if (nameError) {
      errors.cardholderName = nameError;
    }
  }

  // Validate billing address
  if (cardData.billingAddress) {
    const addressErrors = validateBillingAddress(cardData.billingAddress);
    Object.assign(errors, addressErrors);
  } else {
    errors.billingAddress = 'Address Line 1 field is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validates a card number
 * @param cardNumber - Card number string
 * @returns Error message or null if valid
 */
export function validateCardNumber(cardNumber: string): string | null {
  const digits = cardNumber.replace(/\D/g, '');

  if (digits.length === 0) {
    return 'Card Number field is required';
  }
  if (digits.length !== cardNumber.replace(/\s/g, '').length) {
    return 'Unknown card type';
  }
  const cardType = detectCardType(cardNumber);
  if (cardType === 'unknown') {
    return 'Unknown card type';
  }
  const cardTypeName = getCardTypeName(cardType);
  if (!isValidCardLength(cardNumber) || !luhnCheck(cardNumber)) {
    return `Please enter valid ${cardTypeName} card number`;
  }
  return null;
}
/**
 * Validates expiry date
 * @param month - Month string (MM)
 * @param year - Year string (YYYY)
 * @returns Error message or null if valid
 */
export function validateExpiryDate(month: string, year: string): string | null {
  // Validate format: month must be 01-12, year must be 4 digits
  const isValidFormat = /^(0[1-9]|1[0-2])$/.test(month) && /^\d{4}$/.test(year);

  if (!isValidFormat) {
    return 'Expiration date must be in MM/YY format';
  }

  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
  const currentYear = currentDate.getFullYear();

  // Check if expiry date is in the past
  if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
    return 'Card has expired, please enter a valid expiration date';
  }

  // Check if expiry date exceeds 10 years from now
  const maxYear = currentYear + 10;
  if (yearNum > maxYear || (yearNum === maxYear && monthNum > currentMonth)) {
    return 'Card expiration date cannot be more than 10 years from today';
  }

  return null;
}

/**
 * Validates CVV
 * @param cvv - CVV string
 * @param cardType - Card type for expected CVV length
 * @returns Error message or null if valid
 */
export function validateCvv(cvv: string, cardType: string = 'unknown'): string | null {
  const digits = cvv.replace(/\D/g, '');
  if (!/^\d+$/.test(digits)) {
    return 'CVV must contain only digits';
  }
  if (digits.length === 0) {
    return 'CVV field is required';
  }
  const expectedLength = getExpectedCvvLength(cardType as CardType);
  if (digits.length !== expectedLength) {
    return `CVV must be ${expectedLength} digits`;
  }
  return null;
}

/**
 * Validates cardholder name
 * @param name - Cardholder name string
 * @returns Error message or null if valid
 */
export function validateCardholderName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return 'Name on Card field is required';
  }
  if (trimmed.length < CARDHOLDER_NAME_MIN_LENGTH) {
    return `Name on Card must be at least ${CARDHOLDER_NAME_MIN_LENGTH} characters`;
  }
  if (trimmed.length > CARDHOLDER_NAME_MAX_LENGTH) {
    return `Name on card cannot exceed ${CARDHOLDER_NAME_MAX_LENGTH} characters`;
  }
  if (!/^[a-zA-Z0-9 -]+$/.test(trimmed)) {
    return 'Name on card can include only letters, numbers, spaces, and hyphens (-)';
  }
  return null;
}

/**
 * Validates billing address
 * @param address - Billing address object
 * @returns Record of field errors
 */
export function validateBillingAddress(address: Partial<CardData['billingAddress']>): Record<string, string> {
  const errors: Record<string, string> = {};

  const poBoxRegex = /\b[p|P][\s]*[o|O][\s]*[b|B][\s]*[o|O][\s]*[x|X][\s]*[a-zA-Z0-9]*|\b[p|P][O|o].[B|b][O|o][X|x]|\b[p|P].[O|o].[\s]*[B|b][O|o][X|x]\b|\b[P|p]+(OST|ost|o|O)?\.?\s*[O|o|0]+(ffice|FFICE)?\.?\s*[B|b][O|o|0]?[X|x]+\.?\s+[#]?(\d+)*(\D+)*\b/;
  const address1 = address.address1?.trim() ?? '';
  if (!address1) {
    errors.address1 = 'Address Line 1 field is required';
  } else if (poBoxRegex.test(address1)) {
    errors.address1 = 'P.O. Box addresses are not allowed. Please enter a residential address';
  } else if (address1.length > ADDRESS_LINE_MAX_LENGTH) {
    errors.address1 = `Address Line 1 cannot exceed ${ADDRESS_LINE_MAX_LENGTH} characters`;
  }

  const address2 = address.address2?.trim() ?? '';
  if (address2) {
    if (poBoxRegex.test(address2)) {
      errors.address2 = 'P.O. Box addresses are not allowed. Please enter a residential address';
    } else if (address2.length > ADDRESS_LINE_MAX_LENGTH) {
      errors.address2 = `Address Line 2 cannot exceed ${ADDRESS_LINE_MAX_LENGTH} characters`;
    }
  }

  const city = address.city?.trim() ?? '';
  if (!city) {
    errors.city = 'City field is required';
  } else if (city.length > CITY_MAX_LENGTH) {
    errors.city = `City name cannot exceed ${CITY_MAX_LENGTH} characters`;
  } else if (!/^[a-zA-Z '.-]+$/.test(city)) {
    errors.city = 'City must contain only letters, spaces, hyphens, apostrophes, and periods';
  }

  if (!address.state?.trim()) {
    errors.state = 'State field is required';
  } else if (!VALID_STATE_CODES.has(address.state)) {
    errors.state = 'Invalid US state or territory code';
  }

 if (address.postalCode?.trim()) {
    const postalCode = address.postalCode.trim();
    
    // Check max length
    if (postalCode.length > POSTAL_CODE_MAX_LENGTH) {
      errors.postalCode = `Zip Code cannot exceed ${POSTAL_CODE_MAX_LENGTH} characters`;
    } else if (address.country === 'US') {
   
      // Check if it's valid US ZIP format: 12345 or 12345-6789
       if (!/^\d{5}(-\d{4})?$/.test(postalCode)) {
        errors.postalCode = 'Please enter a valid Zip Code';
      }
    }
    // For non-US countries, allow alphanumeric and special characters (no validation)
  } else {
    errors.postalCode = 'Zip Code field is required';
  }
  if (!address.country?.trim()) {
    errors.country = 'Country field is required';
  } else if (address.country !== 'US') {
    errors.country = 'Country must be US';
  }

  return errors;
}

/**
 * Real-time validation for card number as user types
 * @param cardNumber - Current card number input
 * @returns Error message or null if valid so far
 */
export function validateCardNumberLive(cardNumber: string): string | null {
  const digits = cardNumber.replace(/\D/g, '');

  if (digits.length === 0) return null; // Don't show error on empty

  if (digits.length < 13) {
    return 'Card number is incomplete';
  }

  return validateCardNumber(cardNumber);
}

/**
 * Real-time validation for CVV as user types
 * @param cvv - CVV string
 * @param cardType - Card type
 * @returns Error message or null if valid so far
 */
export function validateCvvLive(cvv: string, cardType: string = 'unknown'): string | null {
  const digits = cvv.replace(/\D/g, '');

  if (digits.length === 0) return null; // Don't show error on empty

  const expectedLength = getExpectedCvvLength(cardType as CardType);

  if (digits.length < expectedLength) {
    return 'CVV is incomplete';
  }

  return validateCvv(cvv, cardType);
}

/**
 * Maps CardType values to their display names for error messages
 */
export const BRAND_DISPLAY_NAMES: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
};

/**
* Normalizes brand names (case-insensitive, trims whitespace)
*/
function normalizeBrandName(brand: string): CardType | 'unknown' {

  const normalized = brand.trim().replace(/\s+/g, ' ').toLowerCase();

  switch (normalized) {
    case 'visa':
      return 'visa';
    case 'mastercard':
    case 'master card':
      return 'mastercard';
    case 'amex':
    case 'american express':
      return 'amex';
    case 'discover':
      return 'discover';
    default:
      return 'unknown';
  }
}

/**
 * All supported card brand names as accepted in excludeCards (case-insensitive)
 */
export const ALL_SUPPORTED_BRANDS = ['Visa', 'Mastercard', 'Amex', 'Discover'];

/**
 * Returns an error message if the detected card brand is excluded, otherwise null.
 * Matching is case-insensitive. Returns null for unknown card types.
 * @param detectedType - The detected CardType from cardTypeDetector
 * @param excludedBrands - Array of brand names to block (e.g. ["Amex", "Discover"])
 * @returns Error message string or null if brand is allowed
 */
export function getBrandExclusionError(
  detectedType: CardType,
  excludedBrands: string[]
): string | null {
  if (detectedType === 'unknown' || excludedBrands.length === 0) {
    return null;
  }

  // Normalize excluded brands to canonical internal types before comparison
  const normalizedExcluded = excludedBrands.map(normalizeBrandName);

  const isExcluded = normalizedExcluded.includes(detectedType);

  if (!isExcluded) {
    return null;
  }

  const displayName = BRAND_DISPLAY_NAMES[detectedType] ?? detectedType;
  return `${displayName} cards are not accepted`;
}
